import { useState, useEffect, useRef, useMemo } from "react";
import { fetchFilterMeta } from "./filterOptions.api";
import { applyFilters } from "./applyFilters";
import { UI_MSG } from "../../config/uiMessages";

const EMPTY_META = {
  ofstedBySchoolId: {},
  examSubjectsBySchoolId: {},
  gcseBySchoolId: {},
  ks5BySchoolId: {},
};

/**
 * Fetches filter metadata once, then reactively applies filters
 * whenever schools, filters, or homePosition change.
 *
 * @param {Array}  schools       – raw schools from API
 * @param {Object} filters       – from useFilterState
 * @param {Array|null} homePosition – [lat, lon] for distance filter
 * @returns {{ filteredSchools: Array, filterMeta: Object, filterMetaError: string|null }}
 */
export function useFilteredSchools(schools, filters, homePosition) {
  const [meta, setMeta] = useState(EMPTY_META);
  const [filterMetaError, setFilterMetaError] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    fetchFilterMeta()
      .then((data) => {
        setMeta(data);
        setFilterMetaError(null);
      })
      .catch(() => {
        setMeta(EMPTY_META);
        setFilterMetaError(UI_MSG.FILTER_META_LOAD_FAILED);
      });
  }, []);

  const filteredSchools = useMemo(
    () => applyFilters(schools, filters, meta, { homePosition }),
    [schools, filters, meta, homePosition]
  );

  return { filteredSchools, filterMeta: meta, filterMetaError };
}
